
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Star } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

interface Review {
  id: number; // Changed from string to number to match database bigint type
  rating: number;
  comment: string | null;
  anonymous: boolean;
  created_at: string;
  clients: {
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface PractitionerReviewsProps {
  practitionerId: string;
  averageRating?: number | null;
  totalReviews?: number;
}

const PractitionerReviews: React.FC<PractitionerReviewsProps> = ({
  practitionerId,
  averageRating,
  totalReviews = 0
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const reviewsPerPage = 5;

  useEffect(() => {
    fetchReviews();
  }, [practitionerId, currentPage]);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching reviews for practitioner:', practitionerId);
      
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id,
          rating,
          comment,
          anonymous,
          created_at,
          clients!client_id (
            first_name,
            last_name
          )
        `)
        .eq('practitioner_id', practitionerId)
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * reviewsPerPage, currentPage * reviewsPerPage - 1);

      if (error) {
        console.error('Error fetching reviews:', error);
        return;
      }

      console.log('Fetched reviews:', data);
      
      if (currentPage === 1) {
        setReviews(data || []);
      } else {
        setReviews(prev => [...prev, ...(data || [])]);
      }
      
      setHasMore((data || []).length === reviewsPerPage);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const getClientName = (review: Review) => {
    if (review.anonymous) {
      return 'Anonymous';
    }
    
    if (!review.clients) {
      return 'Unknown User';
    }
    
    const { first_name, last_name } = review.clients;
    return `${first_name || ''} ${last_name || ''}`.trim() || 'Unknown User';
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'text-amber-500 fill-amber-500'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (totalReviews === 0) {
    return (
      <div className="space-y-4">
        <Separator />
        <div>
          <h3 className="text-lg font-medium mb-4">Client Reviews</h3>
          <p className="text-gray-500">No reviews yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Separator />
      <div>
        <h3 className="text-lg font-medium mb-4">Client Reviews</h3>
        
        {/* Rating Summary */}
        {averageRating && (
          <div className="flex items-center space-x-4 mb-6 p-4 bg-amber-50 rounded-lg">
            <div className="flex items-center space-x-2">
              {renderStars(Math.round(averageRating))}
              <span className="text-lg font-medium">{averageRating.toFixed(1)}</span>
            </div>
            <span className="text-gray-600">
              Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Reviews List */}
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-3">
                  {renderStars(review.rating)}
                  <span className="font-medium text-gray-900">
                    {getClientName(review)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {new Date(review.created_at).toLocaleDateString()}
                </span>
              </div>
              
              {review.comment && (
                <p className="text-gray-700 mt-2">{review.comment}</p>
              )}
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {hasMore && reviews.length > 0 && (
          <div className="text-center mt-6">
            <Button
              variant="outline"
              onClick={loadMore}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Load More Reviews'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PractitionerReviews;
